import { Inject, Service } from "typedi";
import { WINDOW } from "../../../../../shared/dependency-injection/InjectionTokens";
import { ApplePayInitError } from "../../../models/errors/ApplePayInitError";
import { IEnhancedApplePayPaymentRequest } from "../../models/apple-pay-payment-data/IEnhancedApplePayPaymentRequest";
import { IApplePaySession } from "../../models/IApplePaySession";
import { IApplePayNamespace } from "../../models/IApplePayNamespace";
import { IApplePaySessionConstructor } from "./models/IApplePaySessionConstructor";

@Service()
export class OfficialApplePaySession implements IApplePayNamespace {
  constructor(@Inject(WINDOW) private window: IWindow) {}

  STATUS_SUCCESS: number;
  STATUS_FAILURE: number;
  STATUS_INVALID_BILLING_POSTAL_ADDRESS: number;
  STATUS_INVALID_SHIPPING_POSTAL_ADDRESS: number;
  STATUS_INVALID_SHIPPING_CONTACT: number;
  STATUS_PIN_INCORRECT: number;
  STATUS_PIN_LOCKOUT: number;
  STATUS_PIN_REQUIRED: number;

  private get applePaySession(): IApplePaySessionConstructor | undefined {
    return this.window.ApplePaySession;
  }

  isApplePaySessionAvailable(): boolean {
    return Boolean(this.applePaySession);
  }

  canMakePayments(): boolean {
    this.assertApplePaySessionIsAvailable();

    return this.applePaySession.canMakePayments();
  }

  canMakePaymentsWithActiveCard(merchantId: string): Promise<boolean> {
    this.assertApplePaySessionIsAvailable();

    return this.applePaySession.canMakePaymentsWithActiveCard(merchantId);
  }

  supportsVersion(version: number): boolean {
    this.assertApplePaySessionIsAvailable();

    return this.applePaySession.supportsVersion(version);
  }

  getLatestSupportedApplePayVersion(): number {
    const versions: number[] = Array.from(Array(15).keys()).slice(1).reverse();

    return versions.find((version: number) => {
      return this.supportsVersion(version);
    });
  }

  createInstance(
    applePayVersion: number,
    applePayPaymentRequest: IEnhancedApplePayPaymentRequest,
  ): IApplePaySession {
    this.assertApplePaySessionIsAvailable();

    return new this.applePaySession(applePayVersion, applePayPaymentRequest);
  }

  private assertApplePaySessionIsAvailable(): void {
    if (!this.isApplePaySessionAvailable()) {
      throw new ApplePayInitError("ApplePaySession is not available.");
    }
  }
}
